import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { Avatar, Button, CssBaseline, TextField, InputAdornment, IconButton } from "@material-ui/core";
import { Box, Typography, Grid, Container, Tooltip } from "@material-ui/core";

// Services
import auth from "../../../../services/auth/auth";

// Components
import AlertComponent from "../../../elements/Alert";
import { DotFalling } from "../../../loader/Loaders";
import TermsOfUse from "../../../sitePolicy/TermsOfUse";

// Icons
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

// Connectors
import backendConnector from "../../../../services/connectors/backendConnector";

// Styles
import SignUpFormStyle from "../../../../assets/styles/materialUI/questionnaire/authentication/signUp/SignUpFormStyle";

// Providers
import { useLanguage } from "../../../../contexts/languageContext";

const SignUpForm = ({ isLoading, setIsLoading, error, setError, message, setMessage }) => {
	const history = useHistory();
	const formRef = React.useRef();
	const { t, i18n } = useTranslation();

	const classes = SignUpFormStyle();
	const { direction } = useLanguage();
	const [count, setCount] = useState(0);

	const [username, setUsername] = useState(null);
	const [emailAddress, setEmailAddress] = useState();
	const [firstName, setFirstName] = useState();
	const [lastName, setLastName] = useState();
	const [organizationName, setOrganizationName] = useState();
	const [title, setTitle] = useState();
	const [password, setPassword] = useState(null);
	const [confirmedPassword, setConfirmedPassword] = useState(null);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
	const [showTermsOfUse, setShowTermsOfUse] = useState(false);

	const signUp = async () => {
		setIsLoading(true);
		setError(null);
		setMessage(null);
		const newAccountDetails = {};
		if (username) newAccountDetails.username = username;
		if (firstName) newAccountDetails.first_name = firstName;
		if (lastName) newAccountDetails.last_name = lastName;
		if (emailAddress) newAccountDetails.email = emailAddress;
		if (organizationName) newAccountDetails.organization_name = organizationName;
		if (title) newAccountDetails.title = title;
		if (password) newAccountDetails.password = password;
		if (confirmedPassword) newAccountDetails.confirmedPassword = confirmedPassword;
		let response = await backendConnector.signUp(newAccountDetails);
		if (response?.message) {
			setMessage(response?.message);
			setCount(10);
		} else {
			setError(response?.error || "common.errors.accountCreationFailed");
		}
		setIsLoading(false);
	};

	const redirectLogin = () => {
		history.push("/questionnaire/login");
	};

	const handleRedirectLogin = (event) => {
		event.preventDefault();
		redirectLogin();
	};

	const validatePassword = (pwd) => {
		const arabicLetterRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
		const arabicDigitRegex = /[\u0660-\u0669\u06F0-\u06F9]/;
		const rules = {
			length: pwd.length >= 8,
			letter: /[A-Za-z]/.test(pwd) || arabicLetterRegex.test(pwd),
			digit: /[0-9]/.test(pwd) || arabicDigitRegex.test(pwd),
			special:
				/[^A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0660-\u0669\u06F0-\u06F9\s]/.test(
					pwd,
				),
		};

		return {
			validPassword: Object.values(rules).every(Boolean),
			rules,
		};
	};

	const validateEmail = (email) => {
		const rules = {
			hasAt: /@/.test(email),
			hasLocalPart: /^[^@]+@/.test(email),
			hasDomain: /@[^@]+\./.test(email),
			hasTLD: /\.[a-zA-Z]{2,}$/.test(email),
			noSpaces: !/\s/.test(email),
			validFormat: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
		};

		return {
			validEmail: Object.values(rules).every(Boolean),
			rules,
		};
	};

	const handleSignUp = () => {
		setError(null);
		if (!formRef.current.reportValidity()) return;
		const { validEmail } = validateEmail(emailAddress);
		const { validPassword } = validatePassword(password);

		if (!auth.isActive()) {
			setError(t("common.licenseExpired"));
			return;
		}
		if (emailAddress && !validEmail) {
			setError(t("auth.signup.errors.invalidEmail"));
			return;
		}
		if (!validPassword) {
			setError(t("auth.signup.errors.weakPassword"));
			return;
		}
		if (password !== confirmedPassword) {
			setError(t("auth.signup.errors.passwordMismatch"));
			return;
		}
		setShowTermsOfUse(true);
	};

	const PasswordTooltipContent = ({ password }) => {
		const { rules } = validatePassword(password || "");

		return (
			<div className={classes.passwordPolicy} dir={direction}>
				<div className={rules.length ? classes.filledPasswordPolicy : classes.pendingPasswordPolicy}>
					{t("auth.signup.passwordRules.length")}
				</div>
				<div className={rules.letter ? classes.filledPasswordPolicy : classes.pendingPasswordPolicy}>
					{t("auth.signup.passwordRules.letter")}
				</div>
				<div className={rules.digit ? classes.filledPasswordPolicy : classes.pendingPasswordPolicy}>
					{t("auth.signup.passwordRules.digit")}
				</div>
				<div className={rules.special ? classes.filledPasswordPolicy : classes.pendingPasswordPolicy}>
					{t("auth.signup.passwordRules.special")}
				</div>
			</div>
		);
	};

	useEffect(() => {
		if (count <= 0) return;

		const timer = setInterval(() => {
			setCount((prevCount) => {
				if (prevCount <= 1) {
					clearInterval(timer);
					history.push("/questionnaire/login");
					return 0;
				}
				return prevCount - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [count, history]);

	const translatedMessage = message ? (i18n.exists(message) ? t(message) : message) : null;

	return (
		<Container component="main" className={classes.signupContainer} dir={direction}>
			<CssBaseline />
			<Grid className={classes.paper}>
				<Avatar className={classes.avatar}>
					<PersonAddIcon />
				</Avatar>
				<Typography variant="h6" color="textSecondary" align="center">
					{t("auth.signup.title")}
				</Typography>
				<form className={classes.form} ref={formRef}>
					<div className={classes.fieldRow}>
						<TextField
							variant="filled"
							margin="normal"
							required
							fullWidth
							id="username"
							label={t("auth.signup.username")}
							name="username"
							value={username}
							onChange={(e) => setUsername(e.target.value.trim())}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSignUp();
							}}
							className={classes.field}
						/>
						<TextField
							variant="filled"
							margin="normal"
							fullWidth
							id="emailAddress"
							label={t("auth.signup.email")}
							name="emailAddress"
							value={emailAddress}
							onChange={(e) => setEmailAddress(e.target.value.trim())}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSignUp();
							}}
							className={classes.field}
						/>
					</div>
					<div className={classes.fieldRow}>
						<TextField
							variant="filled"
							margin="normal"
							fullWidth
							id="firstName"
							label={t("auth.signup.firstName")}
							name="firstName"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value.trim())}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSignUp();
							}}
							className={classes.field}
						/>
						<TextField
							variant="filled"
							margin="normal"
							fullWidth
							id="lastName"
							label={t("auth.signup.lastName")}
							name="lastName"
							value={lastName}
							onChange={(e) => setLastName(e.target.value.trim())}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSignUp();
							}}
							className={classes.field}
						/>
					</div>
					<div className={classes.fieldRow}>
						<TextField
							variant="filled"
							margin="normal"
							fullWidth
							id="organizationName"
							label={t("auth.signup.organization")}
							name="organizationName"
							value={organizationName}
							onChange={(e) => setOrganizationName(e.target.value.trim())}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSignUp();
							}}
							className={classes.field}
						/>
						<TextField
							variant="filled"
							margin="normal"
							fullWidth
							id="title"
							label={t("auth.signup.jobTitle")}
							name="title"
							value={title}
							onChange={(e) => setTitle(e.target.value.trim())}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSignUp();
							}}
							className={classes.field}
						/>
					</div>
					<div className={classes.centerRow}>
						<Tooltip
							title={<PasswordTooltipContent password={password} />}
							placement={direction === "rtl" ? "left-start" : "right-start"}
							arrow
						>
							<TextField
								variant="filled"
								margin="normal"
								required
								fullWidth
								name="password"
								label={t("auth.signup.password")}
								type="text"
								autoComplete="new-password"
								id="password"
								value={password}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label={
													showPassword
														? t("common.buttons.hidePassword")
														: t("common.buttons.showPassword")
												}
												onClick={() => setShowPassword((prev) => !prev)}
												onMouseDown={(e) => e.preventDefault()}
												edge="end"
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								}}
								inputProps={{
									lang: direction === "rtl" ? "ar" : "en",
									dir: "auto",
									inputMode: "text",
									autoCapitalize: "off",
									autoCorrect: "off",
									spellCheck: "false",
									style: {
										WebkitTextSecurity: showPassword ? "none" : "disc",
									},
								}}
								onChange={(e) => setPassword(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleSignUp();
								}}
								className={classes.field}
							/>
						</Tooltip>
						<TextField
							variant="filled"
							margin="normal"
							required
							fullWidth
							name="confirmedPassword"
							label={t("auth.signup.confirmPassword")}
							type="text"
							autoComplete="new-password"
							id="confirmedPassword"
							value={confirmedPassword}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											aria-label={
												showConfirmedPassword
													? t("common.buttons.hidePassword")
													: t("common.buttons.showPassword")
											}
											onClick={() => setShowConfirmedPassword((prev) => !prev)}
											onMouseDown={(e) => e.preventDefault()}
											edge="end"
										>
											{showConfirmedPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}}
							inputProps={{
								lang: direction === "rtl" ? "ar" : "en",
								dir: "auto",
								inputMode: "text",
								autoCapitalize: "off",
								autoCorrect: "off",
								spellCheck: "false",
								style: {
									WebkitTextSecurity: showConfirmedPassword ? "none" : "disc",
								},
							}}
							onChange={(e) => setConfirmedPassword(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSignUp();
							}}
							className={classes.field}
						/>
					</div>
					<div className={classes.submitWrapper}>
						<Button
							fullWidth
							variant="contained"
							color="primary"
							className={`${classes.submit} ${classes.submitButton}`}
							onClick={handleSignUp}
						>
							{t("auth.signup.submit")}
						</Button>
					</div>
				</form>
			</Grid>
			<div className={classes.signinLink}>
				<Box className={classes.signinBox}>
					<Typography align="center">
						{t("auth.signup.signinPrompt")}{" "}
						<a href="#" onClick={handleRedirectLogin}>
							{t("auth.signup.signinLink")}
						</a>
					</Typography>
				</Box>
			</div>
			<Grid className={`${classes.loaderContainer} h-100 w-100 d-flex justify-content-center`}>
				<Grid className={classes.loader}>{isLoading && <DotFalling />}</Grid>
			</Grid>
			<TermsOfUse
				open={showTermsOfUse}
				onClose={() => {
					setShowTermsOfUse(false);
				}}
				onConfirm={() => {
					setShowTermsOfUse(false);
					signUp();
				}}
				source={t("auth.signup.confirm")}
			/>
			{(error || message) && (
				<AlertComponent
					errors={error ? [error] : []}
					messages={
						message
							? [t("auth.signup.redirectCountdown", { message: translatedMessage, seconds: count })]
							: []
					}
					setError={setError}
					setMessage={setMessage}
				/>
			)}
		</Container>
	);
};

export default SignUpForm;
